/**
 * Personal preferences — the contract shared by the API that stores them and
 * the apps that edit them.
 */
import { z } from 'zod';

/** True when `value` is a time zone the runtime knows (IANA, e.g. `America/Sao_Paulo`). */
export function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * An IANA time zone. The citizen apps send the device's own
 * (`Intl.DateTimeFormat().resolvedOptions().timeZone`); quiet hours are
 * evaluated in it, so an unknown value would silently mean UTC.
 */
export const timeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(isTimeZone, 'validation.timeZone.invalid');

/** A time of day, 24h "HH:MM" — what quiet hours are written in. */
export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'validation.timeOfDay.invalid');

/**
 * Quiet hours — pushes wait for the window's end, in the person's time zone.
 * Always saved whole: a malformed start or end used to be accepted and read as
 * midnight, silently moving the window.
 */
export const quietHoursSchema = z.object({
  enabled: z.boolean(),
  start: timeOfDaySchema,
  end: timeOfDaySchema,
});

const bucketFlags = z
  .object({
    hero: z.boolean(),
    system: z.boolean(),
    social: z.boolean(),
    alert: z.boolean(),
  })
  .partial();

const channelCell = z.object({ email: z.boolean(), push: z.boolean() }).partial();

/**
 * `PATCH /users/me/settings` — a partial update of the person's settings, as
 * the API merges it. Every field is optional; what is sent is validated.
 */
export const userSettingsPatchSchema = z.object({
  notifications: z
    .object({
      pushEnabled: z.boolean(),
      emailEnabled: z.boolean(),
      smsEnabled: z.boolean(),
      quietHours: quietHoursSchema,
      /** One switch per category (business-web's preferences); kept beside the matrix. */
      categories: bucketFlags,
      /** Email and push per category — the cells to change. */
      matrix: z
        .object({
          hero: channelCell,
          system: channelCell,
          social: channelCell,
          alert: channelCell,
        })
        .partial(),
    })
    .partial()
    .optional(),
  privacy: z
    .object({
      /** "Share Analytics". */
      shareAnalytics: z.boolean(),
      showInTalentPool: z.boolean(),
    })
    .partial()
    .optional(),
  accessibility: z
    .object({
      reducedMotion: z.boolean(),
      highContrast: z.boolean(),
    })
    .partial()
    .optional(),
  /**
   * App-scoped preferences (per-feature toggles, date format, layout). Each app
   * owns a sub-namespace, deep-merged on update so siblings coexist.
   */
  preferences: z.record(z.string(), z.unknown()).optional(),
});

export type QuietHoursInput = z.infer<typeof quietHoursSchema>;
export type UserSettingsPatch = z.infer<typeof userSettingsPatchSchema>;
